import { useMemo } from 'react'
import PropTypes from 'prop-types'

import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Fade from '@mui/material/Fade'
import Typography from '@mui/material/Typography'

import BrandMark from '@components/Layout/BrandMark'
import { useTabBarClearance } from '@components/Layout/TabBar'

import PageBar from './PageBar'
import { backPropType, columnWidth, PAGE_WIDTH, widthPropType } from './Screen.props'
import { ScreenOptionsProvider, useScreenOptions, useScreenOptionsStore } from './ScreenOptions'

Screen.propTypes = {
  /** Title shown in the bar, and the page's heading */
  title: PropTypes.string,

  /** Supporting copy shown above the page content */
  description: PropTypes.string,

  /** Trailing controls in the bar, for example an add or edit button */
  actions: PropTypes.node,

  /** How the way back behaves. See `backPropType`. */
  back: backPropType,

  /** Width of the content column */
  width: widthPropType,

  /** Puts the logo where the title would go, for the screens shown to signed out users */
  brand: PropTypes.bool,

  /** Element to render as. Anything but `main` is a screen inside something else, such as
   * a dialog, which drops the banner landmark and the tab bar's clearance with it. */
  component: PropTypes.elementType,

  /** Id for the title, for a container that names itself from it */
  titleId: PropTypes.string,

  /** Page content */
  children: PropTypes.node,
}

/**
 * The shell every screen sits in: the `main` landmark, a sticky bar, a centred column.
 *
 * One component for every screen in the app, signed in or out. `AppShell` above it renders
 * navigation and nothing else, so the bar is always inside the screen's own subtree and a
 * screen can configure it by passing props here or with `ScreenOptions` from any child.
 *
 * @param {object} props - The component props.
 * @returns {JSX.Element} The rendered screen.
 */
export default function Screen({
  title,
  description,
  actions,
  back,
  width = PAGE_WIDTH,
  brand = false,
  component = 'main',
  titleId,
  children,
}) {
  const store = useScreenOptionsStore()
  const page = component === 'main'
  const clearance = useTabBarClearance()
  const base = useMemo(() => ({ title, actions, back }), [title, actions, back])

  return (
    <ScreenOptionsProvider value={store}>
      <Box component={component} sx={styles.main(page ? clearance : 0)}>
        <Bar base={base} width={width} brand={brand} landmark={page} titleId={titleId} />

        <Fade in timeout={300}>
          <Container maxWidth={false} sx={[styles.content, columnWidth(width)]}>
            {description && (
              <Typography sx={styles.description} data-testid="page-header-description">
                {description}
              </Typography>
            )}
            {children}
          </Container>
        </Fade>
      </Box>
    </ScreenOptionsProvider>
  )
}

/**
 * The bar, reading the options in force rather than the ones the screen passed. Its own
 * component so a child overriding an option re-renders this and not the rest of the page.
 *
 * @param {object} props - The component props.
 * @returns {JSX.Element|null} The rendered bar, or nothing when there is nothing to put in one.
 */
function Bar({ base, width, brand, landmark, titleId }) {
  const { title, actions, back } = useScreenOptions(base)

  // The native shell draws its own chrome, so a bar carrying only the logo has nothing left
  const suppressed = brand && window.isNativeApp === true
  if (suppressed || !(title || actions || brand || back !== false)) return null

  return (
    <PageBar width={width} back={back} actions={actions} landmark={landmark}>
      {brand ? (
        <BrandMark disabled />
      ) : (
        <Typography variant="h6" component="h1" noWrap id={titleId} data-testid="page-header-title" sx={styles.title}>
          {title}
        </Typography>
      )}
    </PageBar>
  )
}

const styles = {
  // The floating tab bar's clearance sits here rather than on the shell, so a screen that
  // fills the window instead of scrolling the document is free to ignore it. `border-box`
  // keeps it coming out of the height flex handed over rather than adding to it, so a short
  // page ends above the bar instead of scrolling by the width of the band.
  main: (clearance) => ({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0,
    boxSizing: 'border-box',
    pb: clearance,
  }),
  title: {
    minWidth: 0,
    fontWeight: 'bold',
  },
  content: {
    pt: 2,
    pb: 4,
  },
  description: {
    color: 'text.secondary',
    whiteSpace: 'pre-wrap',
    mb: 2,
  },
}
